import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {Localstorage} from 'src/app/domain/service/storage/localstorage';
import {PortfolioSnapshot} from 'src/app/domain/model/portfolio-snapshot.model';

export interface PortfolioSnapshotState {
  portfolioId: string;
  snapshots: PortfolioSnapshot[];
  selected: PortfolioSnapshot | null;
}

export const SELECTED_PORTFOLIO_SNAPSHOT_NAMESPACE = '__selected_portfolio_snapshot__';

const emptyState = () => ({
  portfolioId: '',
  snapshots: [],
  selected: null
});

@Injectable({
  providedIn: 'root',
})
export default class PortfolioSnapshotStore extends ComponentStore<PortfolioSnapshotState> {
  private static readonly snapshotCacheTtlMs = 5 * 60 * 1000;

  public readonly snapshots$ = this.select(state => state.snapshots);
  public readonly portfolioIdOfSnapshots$ = this.select(state => state.portfolioId);
  public readonly selected$ = this.select(state => state.selected);

  private cacheByPortfolioId = new Map<string, {snapshots: PortfolioSnapshot[]; cachedAt: number}>();
  private cacheGeneration = 0;

  constructor(private localStorage: Localstorage) {
    super(emptyState());
  }

  saveAll(portfolioId: string, snapshots: PortfolioSnapshot[]): void {
    this.patchState(() => ({
      portfolioId,
      snapshots
    }));
  }

  saveSelected(selected: PortfolioSnapshot): void {
    this.patchState(() => ({
      selected
    }));
    this.localStorage.syncSet(SELECTED_PORTFOLIO_SNAPSHOT_NAMESPACE, JSON.stringify(selected));
  }

  public saveCache(portfolioId: string, snapshots: PortfolioSnapshot[]): void {
    this.cacheByPortfolioId.set(portfolioId, {snapshots: [...snapshots], cachedAt: Date.now()});
  }

  public syncCache(portfolioId: string): PortfolioSnapshot[] | null {
    const entry = this.cacheByPortfolioId.get(portfolioId);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.cachedAt >= PortfolioSnapshotStore.snapshotCacheTtlMs) {
      this.cacheByPortfolioId.delete(portfolioId);
      return null;
    }

    return [...entry.snapshots];
  }

  public syncCacheGeneration(): number {
    return this.cacheGeneration;
  }

  syncSelected(): PortfolioSnapshot | null {
    return this.get().selected;
  }

  syncPortfolioIdOfSnapshots(): string {
    return this.get().portfolioId;
  }

  syncAll(): PortfolioSnapshot[] | null {
    return this.get().snapshots;
  }

  reset(): void {
    this.patchState(() => ({
      ...emptyState()
    }));
    this.localStorage.unset(SELECTED_PORTFOLIO_SNAPSHOT_NAMESPACE);
  }

  public resetSession(): void {
    this.cacheGeneration++;
    this.cacheByPortfolioId.clear();
    this.reset();
  }
}
